import {EducationSessionErrorReason, Challenge, Scalars, AdaptiveResponse} from "../../schema";
import {ApolloClient} from "@apollo/client";
import {AudioManager} from "../../providers/audio";
import {PlayerReduxAction} from "./rootActions";
import {Action, Scenario} from "../../types";
import {UTM} from "../../libs/analytics";
import {ThunkDispatch} from "redux-thunk";
import {ResponseTimeoutState} from "../../types/ResponseTimeoutState";

export type PlayableAction = Action;

export type DialogueHistoryEntry = {
  action: PlayableAction;
  recognizedText?: string;
  chosenBranchIds?: string[];
  feedbackText?: string;
};

export enum PlayerStage {
  Playing,
  Feedback,
  Result,
}

export type PlayerReduxState = {
  client: ApolloClient<object>; // GraphQL client
  audioManager: AudioManager; // audio playback manager
  toggleRecorder?: (state: boolean) => void; // microphone manager
  raiseProblem?: (key: string) => boolean; // raise problem to detect common failures

  askForCameraPermission?: boolean; // if true, user will be asked to give permission for webcam

  sessionId: Scalars["ID"] | null; // session id if started
  sessionStartFailed?: boolean; // flag will be exist and true, if session not started
  sessionResumeFailed?: boolean; // flag will be exist and true, if session not resumed

  challengeId: Scalars["ID"]; // challenge id from URL, it might be different from challenge.id (contain security hash)
  challenge: Challenge; // challenge object
  scenarios: Scenario[]; // scenarios objects

  isShared: boolean; // is this shared-link session (anonymous)
  isPreview: boolean; // is this preview
  isExternal: boolean; // is external
  externalUserId: Scalars["ID"] | null; // LMS user id
  recognitionEngine?: string; // engine chosen for recognition
  recognitionPipeline?: string; // recognize whole mediafile (old) or chunks (new)

  level: number; // current level number
  index: number; // current action index
  maxIndexes: number[]; // max index of action reached for each scenario

  scenarioIndex: number; // current scenario index, main challenge scenario is 0
  mainScenarioLastIndex: number; // index of main scenario action that was last visited

  // data of previous scenarios
  scenarioStack: Array<{scenarioIndex: number; index: number | null; chosenBranches?: Set<Scalars["ID"]>}>;
  finishedScenarios: Array<string>;

  branchId?: Scalars["ID"];
  branchIndex?: number;
  chosenBranches: Set<Scalars["ID"]>; // branches in current action that were chosen before
  chosenBranchesData: Map<string, string>; // branches in current action that were chosen before

  exposedActions: Scalars["ID"][]; // action ids hint used for

  isRecognizing: boolean;
  isHintConfirmation: boolean;
  isAutoUseHintOffered?: boolean;

  interrupted: boolean;
  interruptReason: null | EducationSessionErrorReason;

  lastUserInputResult: {
    ok: boolean;
    recognizedText?: string;
    errorState: null | EducationSessionErrorReason;
    audioUuid?: string;
  } | null;

  dialogueHistory: DialogueHistoryEntry[];

  currentActionAttempts: number;

  feedbackSent: boolean;

  currentBackground: string | null;
  onPause: boolean;
  onVideoPlay: boolean;
  utm: UTM | null;

  adaptiveInput: AdaptiveInput | null;
  responseTimeoutState: ResponseTimeoutState; // timeout for the current input

  initAgentAction: {
    url?: string;
    text?: string;
    index: number;
    uuid: string;
    played: boolean;
    dismissed?: boolean;
  } | null;
  actionTimerAutoSubmit?: boolean;
  actionTimerRemaining: number | null;
  rpgTurnKey: number;

  stage: PlayerStage;
  isFeedbackPlayed: boolean;
  isFeedbackSkipped: boolean;
};

export type PlayerReduxDispatch = ThunkDispatch<PlayerReduxState, unknown, PlayerReduxAction>;

export enum UserInputDisplayMode {
  EXPECTED_TEXT,
  HINT_TEXT,
  SELECTABLE,
  TEXT_INPUT,
  TEXT_INPUT_SELECTABLE,
}

export type PlayableAdaptiveResponse = AdaptiveResponse & {
  played: boolean;
};

export type AdaptiveInput = {
  id: string;
  uuid?: string;
  isClosed: boolean;
  isError?: boolean;
  responses: PlayableAdaptiveResponse[];
  startedPlaying: boolean;
  feedbackText: string;
};

export const DefaultAdaptiveInput = {
  id: "id",
  uuid: "",
  isClosed: false,
  isError: false,
  responses: [],
  startedPlaying: false,
  feedbackText: "",
};
